/**
 * AB#22360 — the pattern each product on an order is made to, and a reorder's
 * question when the product moved to a newer pattern since.
 */
import { ref } from 'vue';

import { http } from '@/interceptors/axiosRequestInterceptor';

export interface OrderPattern {
  id: number;
  order_item_id: number;
  product_id: number;
  product_name: string | null;
  style_code: string | null;
  factory: { id: number; name: string } | null;
  source:
    | 'order'
    | 'factory'
    | 'none'
    | 'kept'
    | 'latest'
    | 'correction'
    | 'unavailable'
    | 'lead_override';
  recorded_at: string;
  product_now: string | null;
  differs_from_product: boolean;
  repeats: { style_code: string | null; order_item_id: number } | null;
  latest: {
    style_code: string;
    active_from: string | null;
    brought_by:
      | { replacement: number; kind: string | null; from: string; reason: string | null }
      | { development_id: number }
      | null;
  } | null;
  question: 'none' | 'open' | 'answered';
  choice: 'order' | 'latest' | null;
  note: string | null;
  reason: string | null;
  chosen: { by: string | null; at: string } | null;
  can: { choose: boolean; override: boolean };
}

const failure = (e: unknown, fallback: string): string => {
  const d = (e as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
    ?.response?.data;

  return (d?.errors ? Object.values(d.errors)[0]?.[0] : undefined) || d?.message || fallback;
};

export function useOrderPatterns() {
  const items = ref<OrderPattern[]>([]);
  const busy = ref(false);

  const load = async (orderId: number): Promise<void> => {
    try {
      const { data } = await http.get(`orders/${orderId}/patterns`);
      items.value = (data as { result?: { items?: OrderPattern[] } })?.result?.items ?? [];
    } catch {
      // Without access to the order's patterns the line simply does not show.
      items.value = [];
    }
  };

  const choose = async (
    orderId: number,
    patternId: number,
    choice: 'order' | 'latest',
    reason: string | null = null
  ): Promise<{ ok: boolean; message: string }> => {
    busy.value = true;
    try {
      const { data } = await http.post(`order-patterns/${patternId}/choose`, { choice, reason });
      const next = (data as { result?: { items?: OrderPattern[] } })?.result?.items;
      if (next) items.value = next;
      else await load(orderId);

      return { ok: true, message: (data as { message?: string })?.message ?? '' };
    } catch (e) {
      return { ok: false, message: failure(e, 'The pattern was not chosen.') };
    } finally {
      busy.value = false;
    }
  };

  return { items, busy, load, choose };
}
